import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { scholarshipApi } from '../../../lib/api/scholarship.api';
import { errorText } from '../../../lib/error-text';

/** Central roster: fixed program-award becas for a selected program. */
export function ProgramAwardsRoster(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId: tenantId } = useTenantStore();
  const queryClient = useQueryClient();

  const [programs, setPrograms] = useState<Map<string, string>>(new Map());
  const [people, setPeople] = useState<Map<string, string>>(new Map());
  /** Los nombres no se pudieron leer: la lista igual sale, con ids. */
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const [programId, setProgramId] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    const unsub = onSnapshot(collection(db, `tenants/${tenantId}/programs`), (snap) => {
      const m = new Map<string, string>();
      snap.docs.forEach((d) => m.set(d.id, (d.data()['name'] as string | undefined) ?? '—'));
      setPrograms(m);
      if (!programId && snap.docs.length > 0) setProgramId(snap.docs[0]!.id);
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    const unsub = onSnapshot(
      collection(db, `tenants/${tenantId}/people`),
      (snap) => {
        const m = new Map<string, string>();
        snap.docs.forEach((d) => {
          const data = d.data();
          const name = `${data['firstName'] ?? ''} ${data['lastName'] ?? ''}`.trim();
          m.set(d.id, name || d.id);
        });
        setPeople(m);
      },
      (e) => setPeopleError(errorText(e)),
    );
    return () => unsub();
  }, [tenantId]);

  const awardsQuery = useQuery({
    queryKey: ['programAwardsRoster', tenantId, programId],
    queryFn: () => scholarshipApi.listProgramScholarshipAwards({ tenantId: tenantId!, programId }),
    enabled: !!tenantId && !!programId,
  });

  const revokeMutation = useMutation({
    mutationFn: (awardId: string) =>
      scholarshipApi.revokeProgramScholarship({ tenantId: tenantId!, awardId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['programAwardsRoster', tenantId, programId],
      });
    },
  });

  const awards = (awardsQuery.data ?? []).slice().sort((a, b) => {
    // Active first, then by student name.
    if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
    return (people.get(a.studentId) ?? '').localeCompare(people.get(b.studentId) ?? '');
  });

  if (!tenantId) return <></>;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="roster-program-label">{t('scholarshipsPage.roster.program')}</InputLabel>
          <Select
            labelId="roster-program-label"
            label={t('scholarshipsPage.roster.program')}
            value={programs.has(programId) ? programId : ''}
            onChange={(e) => setProgramId(e.target.value)}
          >
            {[...programs.entries()].map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {t('scholarshipsPage.roster.hint')}
        </Typography>
      </Stack>

      {peopleError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {peopleError}
        </Alert>
      )}

      {awardsQuery.isLoading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : awards.length === 0 ? (
        <Alert severity="info">{t('scholarshipsPage.roster.noAwards')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('scholarshipsPage.roster.headers.student')}</TableCell>
                <TableCell>{t('scholarshipsPage.roster.headers.beca')}</TableCell>
                <TableCell align="right">{t('scholarshipsPage.roster.headers.percent')}</TableCell>
                <TableCell>{t('scholarshipsPage.roster.headers.status')}</TableCell>
                <TableCell align="right">{t('scholarshipsPage.roster.headers.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {awards.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/people/${a.studentId}`)}
                    >
                      {people.get(a.studentId) ?? a.studentId}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{a.name}</span>
                      {a.waiveEnrollment && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={t('scholarshipsPage.roster.enrollmentWaived')}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{a.coveragePercent}%</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant={a.status === 'active' ? 'filled' : 'outlined'}
                      color={a.status === 'active' ? 'success' : 'default'}
                      label={
                        a.status === 'active'
                          ? t('scholarshipsPage.roster.statusActive')
                          : t('scholarshipsPage.roster.statusRevoked')
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    {a.status === 'active' && (
                      <Button
                        size="small"
                        color="error"
                        disabled={revokeMutation.isPending}
                        onClick={() => {
                          if (window.confirm(t('scholarshipsPage.roster.confirmRevoke'))) {
                            revokeMutation.mutate(a.id);
                          }
                        }}
                      >
                        {t('scholarshipsPage.roster.revoke')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
